import { debounce } from 'lodash';
import { createContext, FC, ReactNode, useContext, useState } from 'react';
import ConfirmDialog, {
  ConfirmDialogProps,
} from 'shared/components/ConfirmDialog/ConfirmDialog';

type ConfirmDialogContextActions = {
  openDialog: (
    args: Omit<ConfirmDialogProps, 'open' | 'handleClose'> & {
      callback?: (success: boolean) => void;
    }
  ) => void;
};

const ConfirmDialogContext = createContext({} as ConfirmDialogContextActions);

interface ConfirmDialogContextProviderProps {
  children: ReactNode;
}

const ConfirmDialogProvider: FC<ConfirmDialogContextProviderProps> = ({
  children,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [callback, setCallback] = useState<((success: boolean) => void) | null>(
    null
  );

  const [dialogProps, setDialogProps] = useState<Omit<
    ConfirmDialogProps,
    'open' | 'handleClose'
  > | null>(null);

  const resetState = () => {
    setCallback(null);
    setDialogProps(null);
  };

  const openDialog = (
    args: Omit<ConfirmDialogProps, 'open' | 'handleClose'> & {
      callback?: (success: boolean) => void;
    }
  ) => {
    setCallback(() => args.callback);
    setDialogProps(args);

    setOpen(true);
  };

  const handleClose = (result: boolean) => {
    setOpen(false);
    callback && callback(result);

    debounce(() => {
      resetState();
    }, 100);
  };

  return (
    <ConfirmDialogContext.Provider value={{ openDialog }}>
      {children}
      <ConfirmDialog
        {...(dialogProps as ConfirmDialogProps)}
        open={open}
        handleClose={handleClose}
      />
    </ConfirmDialogContext.Provider>
  );
};

const useConfirmDialog = (): ConfirmDialogContextActions => {
  const context = useContext(ConfirmDialogContext);

  if (!context) {
    throw new Error(
      'useConfirmDialog must be used within an ConfirmDialogProvider'
    );
  }

  return context;
};

export { ConfirmDialogProvider, useConfirmDialog };
